/**
 * The widget snapshot — what the home-screen and lock-screen widgets, the App
 * Intents and the icon shortcuts render from, written by the app whenever the
 * ledger changes.
 *
 * Pure: plain rows in, one JSON-ready object out. **Every figure comes from the
 * same domain functions Horizon uses** — `periodBounds()` for the period,
 * `budgetStanding()` for each category with its rollover carry, `periodTotals()`
 * for what was spent, `dailyAllowance()` for a day's share — so a widget can
 * never disagree with the app it opens (INV-2, C-6). Money is integer cents
 * throughout (INV-1).
 *
 * The overall figure is Horizon's: expenditure this period against salary less
 * savings budgets, falling back to expenditure budgets when no salary is in
 * force. Unbudgeted spending counts; savings history does not. Without either
 * ceiling, `leftCents` is null — no high-water mark, which is not zero.
 *
 * The snapshot goes stale at the next local midnight (a period boundary is
 * always a local midnight, so it can never come sooner): the day's share
 * changes then even if nothing is logged, and `validUntil` is when the widget
 * timeline reloads.
 */

import {
  type BudgetRow,
  type CarryContext,
  type RolloverMode,
  budgetStanding,
  baseBudget,
  resolveRolloverMode,
} from "@/domain/budget";
import { isSavingsCategory } from "@/domain/category_kind";
import { salaryInForce, type SalaryRow } from "@/domain/salary";
import { expenseCeiling, savingsCommitted } from "@/domain/savings";
import { type Cents, cents } from "@/domain/money";
import { daysRemaining, localYMD, periodBounds, shiftDays, zonedMidnight } from "@/domain/period";
import { dailyAllowance, periodTotals } from "@/domain/totals";

import type { NativeSnapshot, SnapshotBudget, SnapshotCategory } from "./bridge";

/** The quick amounts are read from this many local days, today included. */
export const QUICK_AMOUNT_DAYS = 30;
export const MAX_QUICK_AMOUNTS = 4;
export const MAX_SNAPSHOT_CATEGORIES = 4;

export interface SnapshotProfile {
  /** The account id — the profile row is keyed on it. */
  readonly id: string;
  readonly currency: string;
  readonly timezone: string;
  readonly cycleStartDay: number;
  readonly rolloverMode: RolloverMode;
  /** Optional until the profile carries one; falls back to the category used last. */
  readonly defaultCategoryId?: string | null;
}

export interface SnapshotCategoryRow {
  readonly isFallback: boolean;
  readonly savingsOrExpenditure?: string | null;
  readonly id: string;
  readonly name: string;
  readonly color: string;
  readonly sortOrder: number;
  readonly archivedAt: string | null;
  readonly deletedAt: string | null;
  readonly rolloverOverride: RolloverMode | null;
}

export interface SnapshotBudgetRow extends BudgetRow {
  readonly categoryId: string;
  readonly deletedAt: string | null;
}

export interface SnapshotExpenseRow {
  readonly categoryId: string;
  readonly occurredAt: string;
  readonly amountCents: Cents;
  readonly deletedAt: string | null;
}

export interface SnapshotInput {
  /** Null when signed out. */
  readonly profile: SnapshotProfile | null;
  readonly categories: readonly SnapshotCategoryRow[];
  readonly budgets: readonly SnapshotBudgetRow[];
  readonly expenses: readonly SnapshotExpenseRow[];
  readonly family?: Omit<SnapshotInput, "family"> & { readonly name: string };
  readonly captureBlocked?: boolean;
  readonly now: Date;
  readonly salaries?: readonly (SalaryRow & { readonly deletedAt: string | null })[];
}

interface Readable extends SnapshotExpenseRow {
  readonly t: number;
}

export function buildWidgetSnapshot(input: SnapshotInput): NativeSnapshot {
  const { profile, now } = input;
  if (profile === null) return { signedOut: true };
  const { timezone, cycleStartDay } = profile;

  // Tombstones are not spending; an unreadable date is counted, not dropped (INV-7).
  const savingsIds = new Set(input.categories.filter(isSavingsCategory).map((c) => c.id));
  const rows: Readable[] = [];
  let unreadable = 0;
  for (const e of input.expenses) {
    if (e.deletedAt !== null || savingsIds.has(e.categoryId)) continue;
    const t = new Date(e.occurredAt).getTime();
    if (Number.isFinite(t)) rows.push({ ...e, t });
    else unreadable += 1;
  }

  const live = input.categories
    .filter((c) => c.deletedAt === null && c.archivedAt === null && !isSavingsCategory(c))
    .sort((a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id));
  const budgets = input.budgets.filter((b) => b.deletedAt === null);
  const period = periodBounds(now, cycleStartDay, timezone);

  const lines: SnapshotBudget[] = live.map((category) => {
    const ctx: CarryContext = {
      budgets: budgets.filter((b) => b.categoryId === category.id),
      expenses: rows.filter((e) => e.categoryId === category.id),
      cycleStartDay,
      timeZone: timezone,
      mode: resolveRolloverMode(profile.rolloverMode, category.rolloverOverride),
    };
    const standing = budgetStanding(ctx, period);
    return {
      categoryId: category.id,
      budgetCents: standing.budget,
      spentCents: standing.spent,
      leftCents: standing.remaining,
      over: standing.isOver,
    };
  });

  const marks = lines.map((l) => l.budgetCents).filter((b): b is number => b !== null);
  const committed = savingsCommitted(input.categories.filter((c) => c.deletedAt === null && c.archivedAt === null && isSavingsCategory(c)).map((c) => ({ isSavings: true, budget: baseBudget(budgets.filter((b) => b.categoryId === c.id), period, timezone) })));
  const ceiling = expenseCeiling(salaryInForce((input.salaries ?? []).filter((s) => s.deletedAt === null), period, timezone), committed);
  const totalBudget = ceiling ?? (marks.length === 0 ? null : cents(marks.reduce((s, b) => s + b, 0)));
  const spent = periodTotals(rows, period).total;
  const left = totalBudget === null ? null : cents(totalBudget - spent);

  const tomorrow = zonedMidnight(shiftDays(localYMD(now, timezone), 1), timezone);
  const validUntil = new Date(Math.min(tomorrow.getTime(), period.end.getTime()));

  const defaultCategory = resolveDefault(profile, live, rows);

  const familyInput = input.family;
  const shared = familyInput ? buildWidgetSnapshot({
    ...familyInput,
    profile: familyInput.profile ? { ...familyInput.profile, defaultCategoryId: profile.defaultCategoryId } : null,
    now,
    family: undefined,
  }) : null;
  const family = familyInput && shared && "v" in shared ? {
    captureBlocked: shared.captureBlocked ?? false,
    family_id: shared.accountId,
    name: familyInput.name,
    currency: shared.currency,
    categories: familyInput.categories
      .filter(c => c.deletedAt === null && c.archivedAt === null && !isSavingsCategory(c))
      .sort((a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id))
      .map(toSnapshotCategory)
      .map(c => ({ ...c, ledger: "family" as const, badge: "👪" as const, name: `${c.name} 👪` })),
    budgets: shared.budgets,
    leftCents: shared.leftCents,
  } : undefined;
  const familyDefault = family?.categories.find(c => c.id === profile.defaultCategoryId);
  return {
    ...(family ? { family } : {}),
    captureBlocked: input.captureBlocked ?? false,
    v: 1,
    accountId: profile.id,
    currency: profile.currency,
    leftCents: left,
    perDayCents: left === null ? null : dailyAllowance(left, daysRemaining(period, now, timezone)),
    daysRemaining: daysRemaining(period, now, timezone),
    over: left !== null && left < 0,
    validUntil: shared && "v" in shared && shared.validUntil < validUntil.toISOString() ? shared.validUntil : validUntil.toISOString(),
    defaultCategory: familyDefault ?? (defaultCategory === null ? null : toSnapshotCategory(defaultCategory)),
    quickAmountsCents: familyDefault && shared && "v" in shared ? shared.quickAmountsCents : defaultCategory === null ? [] : quickAmounts(rows, defaultCategory.id, now, timezone),
    categories: live.slice(0, MAX_SNAPSHOT_CATEGORIES).map(toSnapshotCategory),
    budgets: lines,
    unreadable,
  };
}

function toSnapshotCategory(c: SnapshotCategoryRow): SnapshotCategory {
  return { id: c.id, name: c.name, color: c.color };
}

/**
 * The profile's default if it is still live; otherwise the category used last
 * (what Capture preselects today); otherwise the first in Settings order.
 */
function resolveDefault(
  profile: SnapshotProfile,
  live: readonly SnapshotCategoryRow[],
  rows: readonly Readable[],
): SnapshotCategoryRow | null {
  const byId = new Map(live.map((c) => [c.id, c]));
  const chosen = profile.defaultCategoryId == null ? undefined : byId.get(profile.defaultCategoryId);
  if (chosen) return chosen;

  let latest: Readable | null = null;
  for (const row of rows) {
    if (byId.has(row.categoryId) && (latest === null || row.t > latest.t)) latest = row;
  }
  if (latest !== null) return byId.get(latest.categoryId) ?? null;
  return live[0] ?? null;
}

/** The amounts logged most often in the category lately; ties go to the more recent. */
function quickAmounts(rows: readonly Readable[], categoryId: string, now: Date, timeZone: string): number[] {
  const today = localYMD(now, timeZone);
  const from = zonedMidnight(shiftDays(today, 1 - QUICK_AMOUNT_DAYS), timeZone).getTime();
  const to = zonedMidnight(shiftDays(today, 1), timeZone).getTime();

  const tally = new Map<number, { count: number; last: number }>();
  for (const row of rows) {
    if (row.categoryId !== categoryId || row.t < from || row.t >= to || row.amountCents <= 0) continue;
    const seen = tally.get(row.amountCents) ?? { count: 0, last: 0 };
    tally.set(row.amountCents, { count: seen.count + 1, last: Math.max(seen.last, row.t) });
  }
  return [...tally]
    .sort((a, b) => b[1].count - a[1].count || b[1].last - a[1].last || a[0] - b[0])
    .slice(0, MAX_QUICK_AMOUNTS)
    .map(([amount]) => amount);
}
